"use client";

import { useSyncExternalStore } from "react";

// Shallow search-param state for statically rendered (ISR) pages.
//
// useSearchParams() opts a static page's client tree into client-side
// rendering up to the nearest Suspense boundary, which strips that content
// from the server-rendered HTML crawlers see. This hook renders the default
// (no-param) state on the server and applies the real URL state right after
// hydration. Writes use the native History API, which the App Router
// supports for shallow URL updates (no navigation, no RSC refetch).

const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) listener();
}

function subscribe(callback: () => void) {
  listeners.add(callback);
  window.addEventListener("popstate", callback);
  return () => {
    listeners.delete(callback);
    window.removeEventListener("popstate", callback);
  };
}

export function useShallowSearchParam(key: string): string | null {
  return useSyncExternalStore(
    subscribe,
    () => new URLSearchParams(window.location.search).get(key),
    // Server (and hydration) snapshot: no params. The real value applies
    // immediately after mount without a hydration mismatch.
    () => null
  );
}

// Update one or more params without a navigation. `mode: "replace"` rebuilds
// the query string from only the given entries; `mode: "merge"` (default)
// starts from the current URL. A null value removes the param.
export function setShallowSearchParams(
  params: Record<string, string | null>,
  opts?: { mode?: "merge" | "replace"; history?: "push" | "replace" }
) {
  const url = new URL(window.location.href);
  const next =
    opts?.mode === "replace" ? new URLSearchParams() : url.searchParams;
  for (const [key, value] of Object.entries(params)) {
    if (value === null) next.delete(key);
    else next.set(key, value);
  }
  url.search = next.toString();
  if (opts?.history === "push" && url.href !== window.location.href) {
    window.history.pushState(window.history.state, "", url);
  } else {
    window.history.replaceState(window.history.state, "", url);
  }
  emit();
}
